import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useLiveData } from "@/lib/useLiveData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { CAR_2012_URL, CAR_2010_URL, formatXCD } from "@/lib/constants";
import { Car, ArrowRight, Upload, CheckCircle2 } from "lucide-react";

function days(a, b) {
  if (!a || !b) return 0;
  const d = (new Date(b) - new Date(a)) / 86400000;
  return d > 0 ? Math.ceil(d) : 0;
}

export default function RentalsPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    pickup_date: "", return_date: "",
    driver_name: "", driver_license_number: "",
    license_image_path: "",
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useLiveData(() => {
    api.get("/vehicles").then(r => setVehicles(r.data)).catch(() => {});
  });

  const rentalDays = days(form.pickup_date, form.return_date);
  const total = selected ? rentalDays * selected.rate_per_day : 0;

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!user) { toast.error("Please sign in to upload your license"); return nav("/login"); }
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(s => ({ ...s, license_image_path: data.path }));
      toast.success("License uploaded");
    } catch (err) {
      toast.error("Upload failed: " + (err?.response?.data?.detail || err.message));
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast("Sign in to book"); return nav("/login"); }
    if (!selected) return toast.error("Pick a vehicle");
    if (rentalDays < 1) return toast.error("Pick valid dates");
    if (!form.driver_name || !form.driver_license_number)
      return toast.error("Driver details required");
    setSubmitting(true);
    try {
      const payload = { ...form, vehicle_id: selected.id };
      const { data } = await api.post("/bookings/rental", payload);
      toast.success(`Reservation submitted — ${data.invoice_number}`);
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to book");
    } finally { setSubmitting(false); }
  };

  return (
    <Layout>
      <section className="bg-[#E32636] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#FFCE00]">Division 2</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl mt-3">Affordable vehicle rentals in Antigua.</h1>
          <p className="mt-4 max-w-xl text-white/90">Automatic, air-conditioned and fuel-efficient — EC$130 per day, all-in.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-2 gap-6">
        {vehicles.map(v => (
          <button
            key={v.id}
            data-testid={`vehicle-${v.id}`}
            onClick={() => setSelected(v)}
            className={`text-left bg-white border-2 rounded-md overflow-hidden transition-all hover:-translate-y-1 ${
              selected?.id === v.id ? "border-[#E32636] ring-2 ring-[#E32636]/20" : "border-slate-200 hover:border-slate-400"
            }`}
          >
            <div className="aspect-[16/10] bg-slate-50 flex items-center justify-center p-6">
              <img src={v.image_url} alt={`${v.year} ${v.model}`} className="max-h-full max-w-full object-contain" />
            </div>
            <div className="p-6 border-t border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#1D4ED8]">{v.color}</div>
                  <div className="font-heading font-black text-2xl text-slate-900 mt-1">{v.year} {v.make} {v.model}</div>
                </div>
                <div className="text-right">
                  <div className="font-heading font-black text-2xl text-[#E32636]">{formatXCD(v.rate_per_day)}</div>
                  <div className="text-xs text-slate-500">per day</div>
                </div>
              </div>
              <ul className="mt-4 text-sm text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
                {(v.features || []).map((f) => (
                  <li key={f} className="flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />{f}</li>
                ))}
              </ul>
              {selected?.id === v.id && (
                <div className="mt-4 text-sm font-bold text-[#E32636] flex items-center gap-1">Selected <ArrowRight className="h-4 w-4" /></div>
              )}
            </div>
          </button>
        ))}
      </section>

      {/* Booking form */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="bg-white border border-slate-200 rounded-md p-6 sm:p-8">
          <div className="flex items-center gap-2 text-[#1D4ED8]">
            <Car className="h-5 w-5" strokeWidth={2.5} /><span className="tag-overline !text-[#1D4ED8]">Reservation</span>
          </div>
          <h2 className="font-heading font-black text-2xl mt-1 text-slate-900">Book your rental</h2>

          <form onSubmit={submit} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-bold text-slate-900">Pickup date</Label>
              <Input type="date" data-testid="rental-pickup" value={form.pickup_date}
                     onChange={e => setForm({ ...form, pickup_date: e.target.value })} required />
            </div>
            <div>
              <Label className="text-sm font-bold text-slate-900">Return date</Label>
              <Input type="date" data-testid="rental-return" value={form.return_date}
                     onChange={e => setForm({ ...form, return_date: e.target.value })} required />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-sm font-bold text-slate-900">Driver name</Label>
              <Input data-testid="rental-driver-name" value={form.driver_name}
                     onChange={e => setForm({ ...form, driver_name: e.target.value })} required />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-sm font-bold text-slate-900">Driver's license number</Label>
              <Input data-testid="rental-license-number" value={form.driver_license_number}
                     onChange={e => setForm({ ...form, driver_license_number: e.target.value })} required />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-sm font-bold text-slate-900">Upload driver's license</Label>
              <label className="mt-1 flex items-center justify-center w-full h-28 border-2 border-dashed border-slate-300 rounded-md cursor-pointer hover:border-[#1D4ED8] bg-slate-50">
                <input type="file" accept="image/*,.pdf" onChange={upload} className="hidden" data-testid="rental-license-upload" />
                <div className="text-center text-sm text-slate-600">
                  <Upload className="h-5 w-5 mx-auto text-slate-400" />
                  {uploading ? "Uploading…" : form.license_image_path ? "Uploaded ✓ (click to replace)" : "Click to upload license image or PDF"}
                </div>
              </label>
            </div>

            <div className="sm:col-span-2 flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md p-4">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Total</div>
                <div className="font-heading font-black text-3xl text-slate-900">
                  {rentalDays > 0 && selected ? formatXCD(total) : "—"}
                </div>
              </div>
              <div className="text-right text-sm text-slate-600">
                {selected ? `${selected.year} ${selected.model} · ${selected.color}` : "Pick a vehicle"}
                <div>{rentalDays > 0 ? `${rentalDays} day(s) × EC$130` : "Pick dates"}</div>
              </div>
            </div>

            <div className="sm:col-span-2">
              <Button type="submit" disabled={submitting} data-testid="rental-submit-btn"
                      className="w-full h-12 bg-[#E32636] hover:bg-[#C91F2C] text-white font-bold">
                {submitting ? "Submitting…" : "Submit reservation"}
              </Button>
              {!user && <p className="mt-2 text-xs text-slate-500">You'll be asked to sign in before booking.</p>}
            </div>
          </form>
        </div>
      </section>
    </Layout>
  );
}
